<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import PigmentService from 'services/PigmentService';
import { useAnalytics } from 'src/composables/analytics';

const props = defineProps({
  product: {
    type: Object,
    required: true
  },
  showLabel: {
    type: Boolean,
    default: false
  }
});

const store = useStore();
const { t } = useI18n();
const { trackEvent } = useAnalytics();

const activeUser = computed(() => store.state.user.activeUser);
const token = computed(() => store.getters['user/token']);

const isFavorite = computed(() => {
  if (activeUser.value) {
    return activeUser.value.favoriteProducts
        && Array.isArray(activeUser.value.favoriteProducts)
        && activeUser.value.favoriteProducts.includes(props.product.id.toString());
  }

  return false;
});

const favoriteStateIcon = computed(() => (isFavorite.value ? ['fas', 'star'] : ['far', 'star']));

const label = computed(() => {
  if (!props.showLabel) {
    return null;
  }

  if (isFavorite.value) {
    return t('productSearch.favoriteAction.remove');
  }

  return t('productSearch.favoriteAction.add');
});

const toggleFavorite = async () => {
  if (!activeUser.value) {
    return;
  }

  try {
    const rowId = props.product.id.toString();

    const favorites = new Set(
      activeUser.value.favoriteProducts && Array.isArray(activeUser.value.favoriteProducts)
        ? activeUser.value.favoriteProducts
        : []
    );

    let isAdd = true;

    if (favorites.has(rowId)) {
      isAdd = false;
      favorites.delete(rowId);
    } else {
      favorites.add(rowId);
    }

    trackEvent(
      'Product',
      isAdd ? 'Add Favorite' : 'Remove Favorite',
      {
        productId: rowId,
        industries: props.product.industries,
      }
    );

    await PigmentService.toggleFavorite(rowId, token.value);

    await store.dispatch('user/updateLocalUserInfo', {
      favoriteProducts: [...favorites],
    });
  } catch (e) {
    // noop
  }
};
</script>

<template>
  <button
    class="ce-btn-as-link"
    v-if="activeUser"
    @click.prevent.stop="toggleFavorite">
    <fa-icon :icon="favoriteStateIcon" />

    <span v-if="showLabel">
      {{ label }}
    </span>
  </button>
  <tooltip
    v-else
    :content="$t('productSearch.favoriteAction.loginForFavorites')">
    <button
      class="ce-btn-as-link disabled"
      @click.prevent>
      <fa-icon :icon="favoriteStateIcon" />

      <span v-if="showLabel">
        {{ label }}
      </span>
    </button>
  </tooltip>
</template>
